import {
  type OperationalCalendarInvalidationEvent,
  type OperationalCalendarReadModels,
  operationalCalendarInvalidationEventSchema,
  operationalCalendarReadModelsSchema,
} from "@bednbody/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { StaffRole } from "@/lib/jwt-claims";
import { supabase } from "@/lib/supabase";
import type { CalendarStaffName } from "./calendar-model";
import { addCalendarDays, dayStartIso, latestPublishedEntries } from "./calendar-model";
import { canSubscribeToCalendarRealtime } from "./calendar-scope";

/** Prefix shared by the future staff/booking/dispatch calendar read hooks. */
export const OPERATIONAL_CALENDAR_QUERY_KEY = ["operational-calendar"] as const;

export interface CalendarReadModelResult {
  models: OperationalCalendarReadModels;
  names: CalendarStaffName[];
}

function requireValue<T>(value: T | null, label: string): T {
  if (value === null) throw new Error(`Operational calendar returned an incomplete ${label}.`);
  return value;
}

function bookingKind(value: string | null): "asap" | "scheduled" | "unknown" {
  return value === "asap" || value === "scheduled" ? value : "unknown";
}

/** Reads only the three owner-rights projections plus safe staff labels. */
export function useCalendarReadModels(
  day: string,
  divisionId: string | null,
  hubId: string | null,
) {
  return useQuery<CalendarReadModelResult>({
    queryKey: [...OPERATIONAL_CALENDAR_QUERY_KEY, day, divisionId, hubId],
    queryFn: () => readCalendarRange(day, addCalendarDays(day, 1), divisionId, hubId),
    staleTime: 30_000,
  });
}

/**
 * The three projections for `[fromDay, toDay)` — one day for the old page, a
 * whole week for the board. Only the newest published schedule version counts.
 */
export async function readCalendarRange(
  fromDay: string,
  toDay: string,
  divisionId: string | null,
  hubId: string | null,
): Promise<CalendarReadModelResult> {
  const fromIso = dayStartIso(fromDay);
  const toIso = dayStartIso(toDay);

  let staffQuery = supabase
    .from("v_operational_calendar_staff")
    .select("*")
    .gte("schedule_date", fromDay)
    .lt("schedule_date", toDay);
  let bookingsQuery = supabase
    .from("v_operational_calendar_bookings")
    .select("*")
    .gte("starts_at", fromIso)
    .lt("starts_at", toIso);
  let dispatchesQuery = supabase
    .from("v_operational_calendar_dispatches")
    .select("*")
    .gt("planned_end", fromIso)
    .lt("planned_start", toIso);
  let namesQuery = supabase
    .from("staff")
    .select("id, full_name, hub_id, hubs ( id, name )")
    .in("role", ["therapist", "rider"])
    .eq("active", true)
    .order("full_name");

  if (divisionId) {
    staffQuery = staffQuery.eq("division_id", divisionId);
    bookingsQuery = bookingsQuery.eq("division_id", divisionId);
    dispatchesQuery = dispatchesQuery.eq("division_id", divisionId);
  }
  if (hubId) {
    staffQuery = staffQuery.eq("hub_id", hubId);
    bookingsQuery = bookingsQuery.eq("hub_id", hubId);
    dispatchesQuery = dispatchesQuery.eq("hub_id", hubId);
    namesQuery = namesQuery.eq("hub_id", hubId);
  }

  const [staffResult, bookingsResult, dispatchesResult, namesResult] = await Promise.all([
    staffQuery,
    bookingsQuery,
    dispatchesQuery,
    namesQuery,
  ]);
  if (staffResult.error) throw staffResult.error;
  if (bookingsResult.error) throw bookingsResult.error;
  if (dispatchesResult.error) throw dispatchesResult.error;
  if (namesResult.error) throw namesResult.error;

  const models = operationalCalendarReadModelsSchema.parse({
    staff: latestPublishedEntries(
      (staffResult.data ?? []).map((row) => ({
        scheduleEntryId: requireValue(row.schedule_entry_id, "staff schedule entry"),
        scheduleVersionId: requireValue(row.schedule_version_id, "staff schedule version"),
        hubId: requireValue(row.hub_id, "staff hub"),
        divisionId: row.division_id,
        weekStart: requireValue(row.week_start, "staff week"),
        versionNumber: requireValue(row.version_number, "staff version"),
        publishedAt: requireValue(row.published_at, "staff publication"),
        staffId: requireValue(row.staff_id, "staff member"),
        staffRole:
          row.staff_role === "therapist" || row.staff_role === "rider"
            ? row.staff_role
            : "therapist",
        staffActive: requireValue(row.staff_active, "staff status"),
        scheduleDate: requireValue(row.schedule_date, "staff date"),
        kind: row.kind === "rest_day" ? "rest_day" : "duty",
        dutyStart: row.duty_start,
        dutyEnd: row.duty_end,
      })),
    ),
    bookings: (bookingsResult.data ?? []).map((row) => ({
      bookingId: requireValue(row.booking_id, "booking"),
      ref: requireValue(row.ref, "booking reference"),
      bookingGroupId: row.booking_group_id,
      hubId: requireValue(row.hub_id, "booking hub"),
      divisionId: row.division_id,
      bookingKind: bookingKind(row.booking_kind),
      serviceVariantId: requireValue(row.service_variant_id, "service variant"),
      serviceName: requireValue(row.service_name, "service name"),
      durationMinutes: requireValue(row.duration_minutes, "service duration"),
      startsAt: requireValue(row.starts_at, "booking start"),
      endsAt: requireValue(row.ends_at, "booking end"),
      status: requireValue(row.status, "booking status"),
      therapistId: row.therapist_id,
      riderId: row.rider_id,
      createdAt: requireValue(row.created_at, "booking creation time"),
    })),
    dispatches: (dispatchesResult.data ?? []).map((row) => ({
      dispatchLegId: requireValue(row.dispatch_leg_id, "dispatch leg"),
      dispatchPlanId: requireValue(row.dispatch_plan_id, "dispatch plan"),
      bookingId: requireValue(row.booking_id, "dispatch booking"),
      hubId: requireValue(row.hub_id, "dispatch hub"),
      divisionId: row.division_id,
      planRevision: requireValue(row.plan_revision, "dispatch revision"),
      planStatus: requireValue(row.plan_status, "dispatch plan status"),
      riderId: requireValue(row.rider_id, "dispatch rider"),
      kind: requireValue(row.kind, "dispatch kind"),
      sequence: requireValue(row.sequence, "dispatch sequence"),
      fromLocationId: requireValue(row.from_location_id, "dispatch origin"),
      toLocationId: requireValue(row.to_location_id, "dispatch destination"),
      plannedStart: requireValue(row.planned_start, "dispatch start"),
      plannedEnd: requireValue(row.planned_end, "dispatch end"),
      estimateDistanceKm: row.estimate_distance_km,
      estimateDurationMin: row.estimate_duration_min,
      estimatedAt: row.estimated_at,
      legStatus: requireValue(row.leg_status, "dispatch leg status"),
      createdAt: requireValue(row.created_at, "dispatch creation time"),
    })),
  });

  return {
    models,
    names: (namesResult.data ?? []).map((row) => ({
      id: row.id,
      name: row.full_name,
      hubName: row.hubs?.name ?? null,
    })),
  };
}

export function useCalendarDivisions() {
  return useQuery({
    queryKey: ["calendar-divisions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("divisions")
        .select("id, name")
        .eq("active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

export type CalendarRealtimeStatus = "idle" | "connecting" | "connected" | "error";

export interface CalendarRealtimeOptions {
  enabled?: boolean;
  role: StaffRole;
  /** Hub managers must provide the hub from their verified JWT claim. */
  hubId: string | null;
}

export interface CalendarRealtimeState {
  status: CalendarRealtimeStatus;
  lastInvalidation: OperationalCalendarInvalidationEvent | null;
}

/**
 * Realtime delivers a range signal, not a source-table row. The calendar read
 * hooks remain responsible for fetching the three owner-rights views; one
 * invalidation safely refreshes all of them while preserving the hub/date
 * range in `lastInvalidation` for a recovery banner or focused refetch later.
 */
export function useCalendarRealtimeInvalidation({
  enabled = true,
  role,
  hubId,
}: CalendarRealtimeOptions): CalendarRealtimeState {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<CalendarRealtimeStatus>("idle");
  const [lastInvalidation, setLastInvalidation] =
    useState<OperationalCalendarInvalidationEvent | null>(null);

  useEffect(() => {
    if (!enabled || !canSubscribeToCalendarRealtime(role, hubId)) {
      setStatus("idle");
      return;
    }

    setStatus("connecting");
    const channelName = `operational-calendar:${hubId ?? "all"}`;
    const channel = supabase.channel(channelName);
    const filter = hubId ? `hub_id=eq.${hubId}` : undefined;

    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "operational_calendar_invalidation_events",
        ...(filter ? { filter } : {}),
      },
      (payload) => {
        const parsed = operationalCalendarInvalidationEventSchema.safeParse(payload.new);
        setLastInvalidation(parsed.success ? parsed.data : null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: OPERATIONAL_CALENDAR_QUERY_KEY });
        }, 250);
      },
    );

    void channel.subscribe((nextStatus) => {
      if (nextStatus === "SUBSCRIBED") {
        setStatus("connected");
      } else if (nextStatus === "CHANNEL_ERROR" || nextStatus === "TIMED_OUT") {
        setStatus("error");
      } else if (nextStatus === "CLOSED") {
        setStatus("idle");
      }
    });

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setStatus("idle");
      void supabase.removeChannel(channel);
    };
  }, [enabled, hubId, queryClient, role]);

  return { status, lastInvalidation };
}
